/**
 * @file data-table.tsx
 * @description Generic DataTable обёртка над @tanstack/react-table (phase08.5).
 *
 * Заменяет MUI DataGrid. Поддерживает базовую sorting + pagination + row click.
 * Расширенные фильтры/группировка — пишем в конкретных страницах через хуки
 * react-table.
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   data={rows}
 *   onRowClick={(row) => navigate(`/users/${row.id}`)}
 *   empty="Пользователей нет"
 * />
 * ```
 */

import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import { ListPagination } from "./ListPagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

export type { ColumnDef };

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Текст для пустого состояния. */
  empty?: string;
  /** Callback при клике по строке. */
  onRowClick?: (row: TData) => void;
  /** Размер страницы (по умолчанию 50). Реактивен при передаче pageSizeOptions. */
  pageSize?: number;
  /** Дополнительные классы для table. */
  className?: string;
  /** Скрыть пагинацию (если данных мало или используется внешняя). */
  hidePagination?: boolean;
  /**
   * phase19.5 — включает расширенный футер (ListPagination): выбор per-page +
   * счётчик «X–Y из N». Без него — легаси-футер «Страница N из M».
   */
  pageSizeOptions?: number[];
  /** Колбэк смены per-page (для сохранения выбора страницей). */
  onPageSizeChange?: (size: number) => void;
}

export function DataTable<TData>({
  columns,
  data,
  empty = "Нет данных",
  onRowClick,
  pageSize = 50,
  className,
  hidePagination = false,
  pageSizeOptions,
  onPageSizeChange,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  // Контролируемый per-page: страница меняет prop → таблица подхватывает.
  useEffect(() => {
    if (pageSizeOptions) table.setPageSize(pageSize);
  }, [pageSize, pageSizeOptions, table]);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead key={header.id}>
                    {canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1.5 hover:text-on-surface transition-colors"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {sorted === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : sorted === "desc" ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="py-10 text-center text-on-surface-variant"
              >
                {empty}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn(onRowClick && "cursor-pointer")}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!hidePagination && pageSizeOptions && data.length > 0 ? (
        <ListPagination
          total={data.length}
          pageIndex={table.getState().pagination.pageIndex}
          pageSize={table.getState().pagination.pageSize}
          onPageSizeChange={(size) => {
            table.setPageSize(size);
            onPageSizeChange?.(size);
          }}
          onPrev={() => table.previousPage()}
          onNext={() => table.nextPage()}
          canPrev={table.getCanPreviousPage()}
          canNext={table.getCanNextPage()}
          pageSizeOptions={pageSizeOptions}
        />
      ) : !hidePagination && table.getPageCount() > 1 ? (
        <div className="flex items-center justify-between gap-2 px-1">
          <div className="text-xs text-on-surface-variant">
            Страница {table.getState().pagination.pageIndex + 1} из{" "}
            {table.getPageCount()} · всего {data.length}
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Назад
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Далее
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
